import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { ToolCard } from '@/components/tools/ToolCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { roadmapTools, selfServiceTools } from '@/data/selfServiceTools';
import { useAuth } from '@/lib/auth';

const palette = {
  benzol: '#51BA6D',
  benzolTint: '#EFF7EF',
  benzolBorder: '#BEE0C1',
  surfaceTint: '#F6FBF4',
  surfaceBorder: '#CFE9D3',
  eugeniaTint: '#FCEDEE',
};

export default function SelfServiceToolsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: palette.surfaceTint }}>
      <Sidebar activeItem="tools" user={user} />

      <main id="main-content" className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-10 md:px-10">
          <header className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.surfaceBorder }}>
            <Badge
              className="rounded-full"
              style={{ borderColor: palette.benzolBorder, color: palette.benzol, backgroundColor: palette.benzolTint }}
            >
              Self-Service
            </Badge>
            <h1 className="mt-4 text-3xl font-semibold text-sb-black">
              Launch Studio Bereikbaar tools independently
            </h1>
            <p className="mt-2 text-base text-sb-grey">
              These tools are ready-to-use: log in, follow the guided steps, and export branded
              results without waiting for the data team. Each tool keeps the SB visual language intact.
            </p>
            <div
              className="mt-6 grid gap-4 rounded-2xl border bg-[#F9FAFB] p-4 text-sm text-sb-dark-grey md:grid-cols-3"
              style={{ borderColor: palette.surfaceBorder }}
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Inputs
                </p>
                <p>Upload your datasets or choose defaults per tool.</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Outputs
                </p>
                <p>PDF maps, dashboards, or exports ready for clients.</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Support
                </p>
                <p>Built-in help links. Escalate to the data team only if blocked.</p>
              </div>
            </div>
          </header>

          <section>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-sb-black">Available tools</h2>
                <p className="text-sm text-sb-dark-grey">
                  Click launch to open the tool immediately. No approval or setup required.
                </p>
              </div>
              <Button
                className="rounded-full text-white hover:opacity-90"
                style={{ backgroundColor: palette.benzol }}
                onClick={() => navigate('/dashboard')}
              >
                Back to dashboard
              </Button>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {selfServiceTools.map((tool) => (
                <ToolCard
                  key={tool.id}
                  icon={tool.icon}
                  name={tool.name}
                  status={tool.status}
                  supportLevel={tool.supportLevel}
                  problem={tool.problem}
                  description={tool.description}
                  tags={tool.tags}
                  image={tool.image}
                  onClick={() => tool.link.endsWith('.html') || tool.link.startsWith('http') ? window.location.href = tool.link : navigate(tool.link)}
                />
              ))}
            </div>
          </section>

          {roadmapTools.length > 0 && (
          <section
            className="rounded-3xl border border-dashed bg-white p-6 shadow-sm"
            style={{ borderColor: palette.surfaceBorder }}
          >
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
                Under Review
              </p>
              <h2 className="text-2xl font-semibold text-sb-black">Legacy tools</h2>
              <p className="text-sm text-sb-dark-grey">
                These tools are being validated with the team. We will publish the next five candidates
                after the validation survey closes.
              </p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              {roadmapTools.map((tool) => (
                <div
                  key={tool.id}
                  className="rounded-2xl border p-4 transition"
                  style={{ borderColor: palette.eugeniaTint }}
                >
                  <div className="flex items-center gap-4">
                    <tool.icon size={36} className="text-gray-500" aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {tool.status === 'planned' ? 'Planned' : 'In development'}
                      </p>
                      <h3 className="text-lg font-semibold text-sb-black">{tool.name}</h3>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-sb-dark-grey">{tool.description}</p>
                  <p className="mt-2 text-xs uppercase tracking-wide text-gray-400">
                    Team validation in progress
                  </p>
                </div>
              ))}
            </div>
          </section>
          )}
        </div>
      </main>
    </div>
  );
}
